// Pure helpers for Step 2 (Map Columns). Extracted from Step2MapColumns so they
// can be unit-tested directly (WS-9 prompt §14), following the WS-8 parser-test
// pattern (plain Jest, no React Testing Library needed).

import { classifyWideColumn, findNameColumns, parseNumber } from '../parsers';
import type { ColumnMapping, WizardState } from '../wizardReducer';

// Shape returned by GET /employee-cost/import/fields (Tech Spec §4.1).
export interface FieldDto {
	id: string;
	jsonId: string;
	name: string;
	isActive: boolean;
	isTotal: boolean;
}
export interface SectionDto {
	id: string;
	no: number;
	name: string;
	fields: FieldDto[];
}

// One stored template-mapping entry. Keyed by (sectionNo, fieldName) so templates
// are pay-period-agnostic (Functional Spec §9.2).
export interface TemplateEntry {
	colIndex?: number; // position-based match key; optional for legacy templates predating this
	sourceHeader: string;
	sectionNo: number;
	fieldName: string;
	negate: boolean;
	// ENG-407: an explicit Skip selection the user made. Persisted so reload restores
	// Skip rather than falling back to auto-detection. Skip entries carry no section/
	// field (sectionNo is a sentinel, fieldName empty) and are applied as Skip on load.
	skip?: boolean;
	// ENG-411: an Employee Name column. Persisted so reload restores the user's exact
	// name-column choice (esp. split first/last) rather than relying on auto-detection,
	// which is lossy for non-adjacent / non-standard / manually-picked name columns.
	// Employee Name entries carry no section/field (sectionNo -2 sentinel, fieldName
	// empty); the combined-vs-multi layout is recomputed at Step 3, not persisted.
	employeeName?: boolean;
}

// A saved mapping template as returned (bare row) by the list endpoint.
// NOTE: listTemplates returns a bare array of these, NOT { templates: [...] } as
// drawn in Tech Spec §4.1 — the frontend adapts to the actual implementation.
export interface MappingTemplate {
	id: string;
	name: string;
	mappings: unknown; // raw Json column; parse with parseTemplateEntries
	createdAt?: string;
	updatedAt?: string;
}

const SENTINELS = ['employee_name', 'skip'];

const isSentinel = (category: string) => SENTINELS.includes(category);

// ENG-378 (pass 2): keyword groups for pre-selecting the obvious cost columns into
// the right section, so the user only has to pick the field. First group whose
// keyword appears (case-insensitive substring) in the header wins; that intent is
// then resolved against the company's actual section names. If the intended section
// type doesn't exist for this company, fall back to Skip rather than guessing.
type SectionIntent = 'salary' | 'fringe' | 'tax';

const KEYWORD_INTENTS: Array<{ intent: SectionIntent; keywords: string[] }> = [
	{
		intent: 'salary',
		keywords: ['gross', 'salary', 'wage', 'net pay', 'total earn', 'base pay'],
	},
	{
		intent: 'fringe',
		keywords: [
			'health', 'medical', 'bcbs', 'cobra', 'dental', 'vision', 'aflac',
			'insurance', 'premium', '401', '403', 'retirement', 'pension', 'life ins',
			'disability', 'std', 'ltd', 'gtl', 'fsa', 'hsa', 'er benefit', 'er cost',
			'employer benefit',
		],
	},
	{
		intent: 'tax',
		keywords: [
			'tax', 'taxes', 'socsec', 'social security', 'fica', 'medcare', 'medicare',
			'fitw', 'withhold', 'unemployment', 'futa', 'suta',
		],
	},
];

const sectionMatchesIntent = (name: string, intent: SectionIntent): boolean => {
	const n = name.toLowerCase();
	if (intent === 'salary') return n.includes('salary');
	if (intent === 'fringe')
		return (
			n.includes('fringe') || n.includes('benefit') || n.includes('payroll tax')
		);
	return n.includes('tax');
};

/**
 * Resolve a header to a pre-selected section id via keyword matching, or null when
 * no keyword group matches or the company has no section of the matched intent.
 */
function resolveKeywordSectionId(
	header: string,
	sections: SectionDto[]
): string | null {
	const h = header.toLowerCase();
	const match = KEYWORD_INTENTS.find((group) =>
		group.keywords.some((k) => h.includes(k))
	);
	if (!match) return null;
	const section = sections.find((s) => sectionMatchesIntent(s.name, match.intent));
	return section ? section.id : null;
}

/**
 * Build the initial column-mapping rows on first entry to Step 2 (prompt §7).
 * For each source header: derive sample values, classify the column, and seed the
 * category. Name column(s) auto-assign to Employee Name; everything else starts at
 * Skip. This function itself never keyword-matches a section — ENG-408 moved that
 * into `suggestCategories`, called separately. NOTE: despite ENG-408's original
 * intent that keyword suggestion only run on an explicit "Suggest Categories"
 * click, Step2MapColumns.tsx's load effect (commit 3b707c2, "add auto-select field
 * functionality") now calls `suggestCategories`/`autoSelectSoleField` immediately
 * after this on every first entry to Step 2 too — so in practice, columns matching
 * a keyword no longer stay at Skip once the page finishes loading. Kept here since
 * `buildAutoDetectedMappings` itself is still called from a sheet-switch path that
 * also chains those same two calls (see Step2MapColumns.tsx).
 *
 * Name detection re-uses the parser's `findNameColumns` (the same function Step 1
 * runs) rather than the wizard's `splitNameDetected`, which only carries the
 * *combined* (split Last/First) case. Relying on `splitNameDetected` left
 * single-column name files — ADP ("Employee Name"), Paylocity ("Employee"),
 * Paycom ("eename") — with no auto-mapped name column. Matching by column index
 * also handles files with duplicate header text.
 */
export function buildAutoDetectedMappings(
	headers: string[],
	rawMatrix: string[][],
	headerRowIndex: number
): ColumnMapping[] {
	const dataRows = rawMatrix.slice(headerRowIndex + 1);

	const nameConfig = findNameColumns(headers);
	const nameIndices = new Set<number>();
	if (nameConfig?.type === 'combined') {
		nameIndices.add(nameConfig.lastNameIndex);
		nameIndices.add(nameConfig.firstNameIndex);
	} else if (nameConfig?.type === 'single') {
		nameIndices.add(nameConfig.index);
	}

	return headers.map((header, colIndex) => {
		const sampleValues: string[] = [];
		for (const row of dataRows) {
			const cell = String(row[colIndex] ?? '').trim();
			if (cell !== '') {
				sampleValues.push(cell);
				if (sampleValues.length >= 10) break;
			}
		}
		const classifier = classifyWideColumn(header, sampleValues);
		// ENG-408: name → Employee Name; everything else starts at Skip. Keyword-based
		// section suggestion moved to the on-demand `suggestCategories` helper.
		const category = nameIndices.has(colIndex) ? 'employee_name' : 'skip';
		return {
			colIndex,
			sourceHeader: header,
			category,
			fieldRef: { type: 'none' },
			negate: false,
			classifier,
			sampleValues,
		};
	});
}

/**
 * Applies the ENG-378 keyword matching logic to all columns, replacing their
 * current category. Employee Name columns are never overwritten. Returns a new
 * mappings array (does not mutate). Originally ENG-408 restricted this to an
 * explicit "Suggest Categories" button click only — Step2MapColumns.tsx now also
 * calls it automatically right after `buildAutoDetectedMappings` on first entry to
 * Step 2 and on a sheet switch (see those call sites), in addition to the button
 * still calling it directly for a manual re-suggest.
 */
export function suggestCategories(
	columnMappings: ColumnMapping[],
	sections: SectionDto[]
): ColumnMapping[] {
	return columnMappings.map((m) => {
		if (m.category === 'employee_name') return m; // never overwrite the name column
		const suggested = resolveKeywordSectionId(m.sourceHeader, sections);
		if (!suggested) return m; // no keyword match — leave as-is (Skip)
		// Only suggest if the column is numeric — keyword match on a text/date column
		// is coincidental and not actionable.
		if (m.classifier !== 'numeric') return m;
		return { ...m, category: suggested, fieldRef: { type: 'none' } };
	});
}

/**
 * When a column's chosen section has exactly one active, non-total field, pick it
 * automatically so the user only has to touch columns that are genuinely
 * ambiguous. Columns with no category, a sentinel category, an already-chosen
 * field, or a section with zero/multiple candidate fields are left untouched.
 *
 * Originally chat-only (chatImportHelpers.ts); Step 2 now calls this too so a
 * suggested category also gets its field suggested, not just the category.
 */
export function autoSelectSoleField(
	columnMappings: ColumnMapping[],
	sections: SectionDto[]
): ColumnMapping[] {
	return columnMappings.map((m) => {
		if (isSentinel(m.category)) return m;
		if (m.fieldRef.type !== 'none') return m;
		const section = sections.find((s) => s.id === m.category);
		if (!section) return m;
		const candidates = section.fields.filter((f) => !f.isTotal && f.isActive);
		if (candidates.length !== 1) return m;
		return { ...m, fieldRef: { type: 'existing', fieldId: candidates[0].id } };
	});
}

/**
 * Auto-defaults Convert (negate) ON for a numeric column whose sampled values are
 * ALL negative — either signed ("-150.00") or accounting-bracket ("(150.00)")
 * notation, both already normalized by parseNumber. This is a strong signal the
 * column is an inherently negative-convention one (e.g. a credit/adjustment
 * line), distinct from the documented Convert use case of a positive-sourced
 * deduction column the user flips by hand.
 *
 * Deliberately conservative: even a single non-negative (positive or zero)
 * sampled value leaves the column untouched. Convert flips every row in the
 * column, so a false positive here (e.g. one mostly-positive column with a
 * couple of negative outlier/adjustment rows) would silently corrupt every
 * other row's sign on import — worse than asking the user to flip it by hand.
 * Only touches columns still at the untouched default (negate: false), so a
 * user's explicit choice or a value restored from a loaded template is never
 * overridden.
 */
export function applyAutoNegateDetection(
	columnMappings: ColumnMapping[]
): ColumnMapping[] {
	return columnMappings.map((m) => {
		if (m.classifier !== 'numeric' || m.negate) return m;
		const parsed = m.sampleValues
			.map((v) => parseNumber(v))
			.filter((n) => Number.isFinite(n));
		if (parsed.length === 0) return m;
		const allNegative = parsed.every((n) => n < 0);
		return allNegative ? { ...m, negate: true } : m;
	});
}

/**
 * Column indexes with a real category selected but no field mapped yet — the same
 * set `getStep2ValidationError`'s "N column(s) have a category selected but no
 * field mapped" message counts. Exposed separately so the UI can scroll to /
 * highlight exactly those rows instead of just naming a count.
 */
export function getIncompleteColumnIndexes(columnMappings: ColumnMapping[]): number[] {
	return columnMappings
		.filter((m) => !isSentinel(m.category) && m.fieldRef.type === 'none')
		.map((m) => m.colIndex);
}

/** jsonId derivation for a new field label (Functional Spec §13.11). */
export function deriveJsonId(label: string): string {
	return label
		.trim()
		.replace(/[^a-zA-Z0-9]+/g, '_')
		.replace(/^_|_$/g, '')
		.toLowerCase();
}

/**
 * Build the (sectionNo, fieldName, sourceHeader, negate)[] payload to persist as
 * a template (Functional Spec §9.2). Skip selections are persisted as explicit
 * skip entries (ENG-407) so reload restores them; Employee Name is omitted (it is
 * auto-detected on load). Unmapped cost rows are dropped. Pending fields contribute
 * their typed label as fieldName.
 */
export function buildMappingsPayload(
	columnMappings: ColumnMapping[],
	sections: SectionDto[],
	pendingFields: WizardState['pendingFields']
): TemplateEntry[] {
	const entries: TemplateEntry[] = [];
	for (const m of columnMappings) {
		// ENG-407: persist explicit Skip selections so they survive save/reload.
		if (m.category === 'skip') {
			entries.push({
				colIndex: m.colIndex,
				sourceHeader: m.sourceHeader,
				sectionNo: -1, // sentinel: Skip carries no section
				fieldName: '', // Skip carries no field
				negate: false,
				skip: true,
			});
			continue;
		}
		// ENG-411: persist Employee Name columns (one entry per column) so reload
		// restores the user's exact choice instead of re-running lossy auto-detection.
		if (m.category === 'employee_name') {
			entries.push({
				colIndex: m.colIndex,
				sourceHeader: m.sourceHeader,
				sectionNo: -2, // sentinel: Employee Name carries no section (distinct from Skip's -1)
				fieldName: '', // Employee Name carries no field
				negate: false,
				employeeName: true,
			});
			continue;
		}
		const ref = m.fieldRef; // capture so narrowing survives the find() closures
		if (ref.type === 'none') continue;
		const section = sections.find((s) => s.id === m.category);
		if (!section) continue;
		let fieldName: string | undefined;
		if (ref.type === 'existing') {
			fieldName = section.fields.find((f) => f.id === ref.fieldId)?.name;
		} else {
			fieldName = pendingFields.find((p) => p.tempId === ref.tempId)?.label;
		}
		if (!fieldName) continue;
		entries.push({
			colIndex: m.colIndex,
			sourceHeader: m.sourceHeader,
			sectionNo: section.no,
			fieldName,
			negate: m.negate,
		});
	}
	return entries;
}

// Below this ratio of a saved mapping's headers actually appearing in the new
// file, applying it silently would be more confusing than helpful — most of
// its entries would just reset to Skip (resolveTemplateToMappings treats an
// unmatched column as authoritative-Skip), while the ones that DO happen to
// match by header name might belong to an unrelated document. Used by the
// silent "last used" default mapping (SmartImportDefaultMapping on the
// backend) — a named template the user explicitly picked has no such gate.
export const DEFAULT_MAPPING_MIN_HEADER_OVERLAP = 0.5;

/**
 * Fraction of `templateEntries` whose source header appears (case-insensitive,
 * trimmed) among the new file's headers. Used to decide whether a saved
 * mapping looks like it actually matches this file before applying it
 * silently, rather than blindly applying one saved from a different document
 * type just because a company has exactly one remembered mapping.
 */
export function computeHeaderOverlapRatio(
	headers: string[],
	templateEntries: TemplateEntry[]
): number {
	if (templateEntries.length === 0) return 0;
	const headerSet = new Set(headers.map((h) => h.trim().toLowerCase()));
	const matched = templateEntries.filter((e) =>
		headerSet.has(e.sourceHeader.trim().toLowerCase())
	).length;
	return matched / templateEntries.length;
}

/** Defensive parse of a template's raw `mappings` Json into typed entries. */
export function parseTemplateEntries(mappings: unknown): TemplateEntry[] {
	if (!Array.isArray(mappings)) return [];
	const out: TemplateEntry[] = [];
	for (const e of mappings) {
		if (
			e &&
			typeof e === 'object' &&
			typeof (e as TemplateEntry).sourceHeader === 'string' &&
			typeof (e as TemplateEntry).sectionNo === 'number' &&
			typeof (e as TemplateEntry).fieldName === 'string'
		) {
			out.push({
				sourceHeader: (e as TemplateEntry).sourceHeader,
				sectionNo: (e as TemplateEntry).sectionNo,
				fieldName: (e as TemplateEntry).fieldName,
				negate: !!(e as TemplateEntry).negate,
				...(typeof (e as TemplateEntry).colIndex === 'number'
					? { colIndex: (e as TemplateEntry).colIndex }
					: {}),
				...((e as TemplateEntry).skip ? { skip: true } : {}),
				...((e as TemplateEntry).employeeName ? { employeeName: true } : {}),
			});
		}
	}
	return out;
}

/**
 * Apply a loaded template's entries onto the current column mappings, resolving
 * (sectionNo, fieldName) → fieldId against the current pay period's sections
 * (Functional Spec §9.3, exact case-insensitive name match). The template is the
 * authoritative starting state (ENG-407): columns with no matching template entry
 * are reset to Skip (Employee Name preserved) so auto-detected categories don't
 * bleed through. Columns whose entry can't be resolved are reset to Skip and
 * reported in `unresolved` (by colIndex) so the UI can flag them for remapping
 * (no import block).
 */
export function resolveTemplateToMappings(
	currentMappings: ColumnMapping[],
	templateEntries: TemplateEntry[],
	sections: SectionDto[]
): {
	mappings: ColumnMapping[];
	unresolved: number[];
	pendingFieldsToCreate: Array<{
		tempId: string;
		label: string;
		sectionId: string;
		derivedJsonId: string;
		colIndex: number;
	}>;
	outOfRange: number;
} {
	const unresolved: number[] = [];
	const pendingFieldsToCreate: Array<{
		tempId: string;
		label: string;
		sectionId: string;
		derivedJsonId: string;
		colIndex: number;
	}> = [];
	// Consume each template entry at most once so duplicate-header columns can't
	// all claim the same entry (R2-5). Declared outside .map() to persist across
	// column iterations.
	const matchedEntries = new Set<TemplateEntry>();
	// Dedup reconstructed pending fields by (sectionId, fieldName) within this load.
	// A template saved before the "Field already exists" guard existed can carry two
	// or more entries sharing the same (sectionNo, fieldName) — e.g. FED SOCSEC and
	// FED MEDCARE both mapped to "Payroll Taxes" in section 2. Without this, each
	// such column mints its own pending field (a distinct tempId per colIndex), and
	// the import then creates duplicate columns for one logical field. Reusing a
	// single shared tempId makes all duplicates resolve to ONE pending field — the
	// same outcome as the manual "Use Existing" path (handleConfirmNewField) — while
	// each source column keeps its own mapping row. Handles N duplicates (ENG-373).
	const pendingTempIdByKey = new Map<string, string>();
	// ENG-411: when the template carries Employee Name entries it is authoritative for
	// name columns — auto-detected name columns not in the template are reset to Skip.
	// Legacy templates (saved before ENG-411, no employeeName entries) keep the prior
	// preserve-auto-detected behavior so their required name mapping isn't wiped.
	const templateHasEmployeeName = templateEntries.some((e) => e.employeeName);
	const headerKey = (h: string) => h.trim().toLowerCase();
	const mappings = currentMappings.map((m): ColumnMapping => {
		// ENG-407 (R4): match by header NAME first (case-insensitive, trimmed) so a file
		// with reordered or removed columns maps to the correct fields instead of
		// whatever field sat at that position in the original file (the previous
		// position-first logic silently mis-mapped on layout changes). colIndex is used
		// ONLY to disambiguate duplicate headers — an entry that matches BOTH the header
		// and the position beats a same-header entry at a different position — never as a
		// bare position fallback. Skip already-consumed entries so duplicate-header
		// columns can't all claim the same one (R2-5).
		const entry =
			templateEntries.find(
				(e) =>
					!matchedEntries.has(e) &&
					e.colIndex !== undefined &&
					e.colIndex === m.colIndex &&
					headerKey(e.sourceHeader) === headerKey(m.sourceHeader)
			) ??
			templateEntries.find(
				(e) =>
					!matchedEntries.has(e) &&
					headerKey(e.sourceHeader) === headerKey(m.sourceHeader)
			);
		if (!entry) {
			// ENG-407: no template entry for this column → reset to a clean Skip so
			// auto-detected categories don't bleed through and the template is the
			// authoritative starting state. ENG-411: preserve an auto-detected Employee
			// Name column only for legacy templates that carry no name entries — wiping
			// it there would drop the required name mapping and block Next. When the
			// template DOES carry name entries it is authoritative, so an auto-detected
			// name column absent from it is reset to Skip (the saved choice wins).
			if (m.category === 'employee_name' && !templateHasEmployeeName) return m;
			return { ...m, category: 'skip', fieldRef: { type: 'none' }, negate: false };
		}
		matchedEntries.add(entry); // consume it — no other column can claim this entry
		// ENG-411: an Employee Name entry restores the column as Employee Name (no field).
		if (entry.employeeName) {
			return {
				...m,
				category: 'employee_name',
				fieldRef: { type: 'none' },
				negate: false,
			};
		}
		// ENG-407: an explicit Skip entry restores the column to Skip (no field).
		if (entry.skip) {
			return { ...m, category: 'skip', fieldRef: { type: 'none' }, negate: false };
		}
		const section = sections.find((s) => s.no === entry.sectionNo);
		const field = section?.fields.find(
			(f) =>
				f.name.trim().toLowerCase() === entry.fieldName.trim().toLowerCase()
		);
		if (section && field) {
			return {
				...m,
				category: section.id,
				fieldRef: { type: 'existing', fieldId: field.id },
				negate: entry.negate,
			};
		}
		// Section resolves but the field isn't in the DB — likely a pending field
		// the template carried that was never persisted (the user abandoned before
		// Step 4 Confirm). Reconstruct it rather than dropping to Skip (Fix 6).
		if (section && entry.fieldName.trim() !== '') {
			// Reuse a pending field already reconstructed for this (section, field)
			// so duplicate entries collapse onto one new field instead of many.
			const key = `${section.id}__${entry.fieldName.trim().toLowerCase()}`;
			let tempId = pendingTempIdByKey.get(key);
			if (tempId === undefined) {
				tempId = `__template_pending__${m.colIndex}`;
				pendingTempIdByKey.set(key, tempId);
				pendingFieldsToCreate.push({
					tempId,
					label: entry.fieldName,
					sectionId: section.id,
					derivedJsonId: deriveJsonId(entry.fieldName),
					colIndex: m.colIndex,
				});
			}
			return {
				...m,
				category: section.id,
				fieldRef: { type: 'pending', tempId },
				negate: entry.negate,
			};
		}
		unresolved.push(m.colIndex);
		return { ...m, category: 'skip', fieldRef: { type: 'none' }, negate: false };
	});
	// Template entries whose stored position lies beyond this file's columns —
	// a layout-changed signal distinct from name/field resolution failures (R2-6).
	const outOfRange = templateEntries.filter(
		(e) => e.colIndex !== undefined && e.colIndex >= currentMappings.length
	).length;
	return { mappings, unresolved, pendingFieldsToCreate, outOfRange };
}

/**
 * Returns the inline validation message that blocks Next, or null when Step 2 is
 * valid (Functional Spec §4.10).
 */
export function getStep2ValidationError(
	columnMappings: ColumnMapping[]
): string | null {
	const hasEmployeeName = columnMappings.some(
		(m) => m.category === 'employee_name'
	);
	if (!hasEmployeeName) return 'Map a column to Employee Name to continue.';

	// A column with a real (non-sentinel) category but no field selected would be
	// silently dropped at import (Kyle, UAT R2 / ENG-380). Block Next and name the
	// count so the user knows how many rows to finish. Checked before the
	// "at least one cost field" rule so its more specific, actionable message wins
	// when a column is started-but-incomplete rather than untouched.
	const incompleteCount = columnMappings.filter(
		(m) => !isSentinel(m.category) && m.fieldRef.type === 'none'
	).length;
	if (incompleteCount > 0) {
		const plural = incompleteCount === 1;
		return `${incompleteCount} column${plural ? '' : 's'} ${
			plural ? 'has' : 'have'
		} a category selected but no field mapped.`;
	}

	const hasCostField = columnMappings.some(
		(m) => !isSentinel(m.category) && m.fieldRef.type !== 'none'
	);
	if (!hasCostField) return 'Map at least one cost field to continue.';
	return null;
}
